import { useState } from "react";
import { Alert, Badge, Button, DataTable, Field, Input, Select } from "@empite/ui-components";
import type { ModelAssignment, ProviderModel } from "../admin-client";
import { useAssignModels, useProviderModels } from "../admin-hooks";
import { ErrorAlert } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge } from "./common";

/**
 * "Which models may this application use?" — answered by ticking them.
 *
 * The whole point is what it does NOT ask. A model policy has an alias, a rank,
 * three token ceilings and four capability gates, and none of them is a
 * decision this screen should be extracting from somebody who wants to say
 * "this product may use Sonnet":
 *
 *   * the ALIAS lives on the catalogue row, decided once when the model was
 *     added, by the person who was looking at the vendor's id at the time;
 *   * the RANK appends — a model being added is being added behind whatever
 *     already serves that name;
 *   * the CEILINGS stay unset, because a blank means the profile's own limits
 *     apply. An application-level ceiling is a NARROWING of the profile's, and
 *     most assignments do not want one. Leaving them empty is the correct
 *     answer rather than a shortcut.
 *
 * Tuning any of that is the routing-profile screen's job, where the fallback
 * chain is visible and the numbers mean something next to each other.
 */
export function AssignModels({
  profileId,
  assignedModelIds,
  onClose,
  onDone,
}: {
  profileId: string;
  /** Catalogue ids this profile already serves, so the list can say so rather
   *  than offering a tick that would come back "already assigned". */
  assignedModelIds: ReadonlySet<string>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const catalogue = useProviderModels();
  const assign = useAssignModels();
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [filter, setFilter] = useState("");
  const [provider, setProvider] = useState("");
  const [outcomes, setOutcomes] = useState<ModelAssignment[] | null>(null);

  const rows = (catalogue.data?.models ?? [])
    // A retired model cannot be assigned to anything, and offering it is
    // offering a tick that the server will refuse.
    .filter((row) => row.status !== "retired")
    .filter((row) => provider === "" || row.provider === provider);

  const needle = filter.trim().toLowerCase();
  const shown =
    needle === ""
      ? rows
      : rows.filter(
          (row) =>
            row.alias.toLowerCase().includes(needle) ||
            row.model.toLowerCase().includes(needle) ||
            row.display_name.toLowerCase().includes(needle),
        );

  const providers = [...new Set((catalogue.data?.models ?? []).map((row) => row.provider))].sort();
  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <div className="esm-subform">
      <h3>Assign models to this application</h3>
      <p className="esm-muted-text">
        Tick the catalogue models this application may call. Nothing else is needed: each model
        already carries the name customers send for it, and the profile's own token limits apply
        unless you narrow them on the routing profile.
      </p>
      <ErrorAlert error={catalogue.error} title="The catalogue could not be read" />
      <ErrorAlert error={assign.error} title="Those models could not be assigned" />

      {outcomes === null ? null : (
        <AssignmentReport outcomes={outcomes} catalogue={catalogue.data?.models ?? []} />
      )}

      <div className="esm-form-grid">
        <Field
          label="Find a model"
          htmlFor="assign-filter"
          hint="Matches the name customers send, the provider's id, or the display name."
        >
          <Input
            id="assign-filter"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </Field>
        <Field label="Provider" htmlFor="assign-provider" hint="Narrows the list to one vendor.">
          <Select
            id="assign-provider"
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
          >
            <option value="">Every provider</option>
            {providers.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <DataTable<ProviderModel>
        rows={shown}
        rowKey={(row) => row.id}
        empty="No catalogue model matches."
        columns={[
          {
            key: "pick",
            header: "",
            render: (row) =>
              assignedModelIds.has(row.id) ? (
                <Badge tone="success">assigned</Badge>
              ) : (
                <input
                  type="checkbox"
                  aria-label={`Assign ${row.display_name || row.model}`}
                  checked={picked.has(row.id)}
                  onChange={() => toggle(row.id)}
                />
              ),
          },
          {
            key: "model",
            header: "Model",
            // The display NAME leads. An operator choosing what a product may
            // use is picking a model, not composing a routing decision — the
            // name customers send is settled on the catalogue row and is not a
            // choice being made here, so putting it in front would be showing
            // vocabulary this screen exists to avoid.
            render: (row) => (
              <>
                <div>{row.display_name || row.model}</div>
                <span className="esm-muted-text">
                  <code>{row.model}</code> on <code>{row.provider}</code>
                </span>
              </>
            ),
          },
          {
            key: "status",
            header: "Status",
            render: (row) => <StatusBadge status={row.status} />,
          },
        ]}
      />

      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={assign.isPending}
          disabled={picked.size === 0}
          mutation={{
            title: `Assign ${picked.size} model${picked.size === 1 ? "" : "s"}`,
            tone: "primary",
            confirmLabel: "Assign",
            description: (
              <p>
                This application's clients will be able to call{" "}
                {picked.size === 1 ? "this model" : `these ${picked.size} models`}. Availability
                stays as it is — a newly assigned model is released to everybody unless you hold
                it back afterwards.
              </p>
            ),
            run: async (reason) => {
              const result = await assign.mutateAsync({
                profileID: profileId,
                modelIDs: [...picked],
                reason,
              });
              setPicked(new Set<string>());
              setOutcomes(result.assignments);
              const assigned = result.assignments.filter((a) => a.outcome === "assigned").length;
              const skipped = result.assignments.filter((a) => a.outcome === "skipped").length;
              onDone(
                skipped === 0
                  ? `Assigned ${assigned} model${assigned === 1 ? "" : "s"}.`
                  : `Assigned ${assigned}, skipped ${skipped} — see why below.`,
              );
            },
          }}
        >
          Assign {picked.size === 0 ? "" : picked.size}
        </ReasonButton>
        <Button variant="ghost" disabled={assign.isPending} onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

/**
 * What happened to each tick.
 *
 * Skips are shown with their reason and are never folded into a count. An
 * operator who ticked six and got four has to be told which two and why, or
 * they leave believing all six are assigned — which is exactly the belief that
 * ends with a customer's request being refused for a model the console said
 * they had.
 */
function AssignmentReport({
  outcomes,
  catalogue,
}: {
  outcomes: ModelAssignment[];
  catalogue: ProviderModel[];
}) {
  // Named the way the picker named them a moment ago. The response carries ids
  // and the alias; an operator who ticked "Gemini 3.1 Pro" should be told about
  // "Gemini 3.1 Pro" and not about gemini-3.1-pro.
  const nameOf = (modelID: string, fallback: string) =>
    catalogue.find((row) => row.id === modelID)?.display_name || fallback;

  const skipped = outcomes.filter((a) => a.outcome === "skipped");
  const assigned = outcomes.filter((a) => a.outcome === "assigned");
  const already = outcomes.filter((a) => a.outcome === "already_assigned");

  return (
    <Alert
      tone={skipped.length > 0 ? "warning" : "success"}
      title={
        skipped.length > 0
          ? `${assigned.length} assigned, ${skipped.length} skipped`
          : `${assigned.length + already.length} assigned`
      }
    >
      {skipped.length === 0 ? (
        "Every model you ticked is now available to this application."
      ) : (
        <ul>
          {skipped.map((a) => (
            <li key={a.model_id}>
              {nameOf(a.model_id, a.alias)} (<code>{a.provider_model}</code> on {a.provider}) —{" "}
              {a.reason}
            </li>
          ))}
        </ul>
      )}
    </Alert>
  );
}
